import { nextLesson } from '../../stores/next-lesson';
import type { LessonWithProgress } from '../../types';

/**
 * The round marker a lesson shows on the Course page's dotted rail
 * (redesign PR4). Order matters: a lock wins over everything, then the
 * result, then whether this is the chapter's next step.
 *   locked   grey lock
 *   perfect  gold star (and the graduated giraffe)
 *   done     green check
 *   current  orange book: the one clear next step in the chapter
 *   open     plain book: open, not passed, not the next step
 */
export type LessonMarker = 'locked' | 'perfect' | 'done' | 'current' | 'open';

export function lessonMarker(lesson: LessonWithProgress, isNext: boolean): LessonMarker {
  if (lesson.locked) return 'locked';
  // The card has always drawn a perfect result from results.is_perfect alone.
  if (lesson.results?.is_perfect) return 'perfect';
  if (lesson.passed) return 'done';
  return isNext ? 'current' : 'open';
}

/**
 * The chapter's next step, by the store's rule (stores/next-lesson.ts): the
 * lesson in progress, else the first open one never tried. The same rule
 * sets the rows' scroll anchors, so the orange card is the scroll target.
 */
export function nextLessonId(lessons: LessonWithProgress[]): number | null {
  return nextLesson(lessons)?.id ?? null;
}

/**
 * The lesson row the Course page scrolls to once lessons load: the first
 * one in progress, else the first next one, else the last "last" fallback
 * (every lesson done). Reads the rows' data-scroll-priority anchors.
 */
export function scrollTargetLesson(root: ParentNode): Element | null {
  const inProgress = root.querySelector('[data-scroll-priority="in-progress"]');
  if (inProgress) return inProgress;
  const next = root.querySelector('[data-scroll-priority="next"]');
  if (next) return next;
  const last = root.querySelectorAll('[data-scroll-priority="last"]');
  return last.length > 0 ? last[last.length - 1] : null;
}
